import { useCallback, useMemo, useState } from "react";
import {
  Card,
  EmptyState,
  PageHeader,
  useEntityDelete,
  useEntityDetail,
  useUpdateEntity,
  type UiEntitySpec,
} from "@pericles/framework-frontend";
import type { RequirementChecklistItem, RequirementGeneratedArtifacts, WorkplanItem } from "@pericles/agent-orchestrator";
import { AI_REVIEW_STATUS, type AiReviewStatus } from "@pericles/shared";

import {
  AI_REVIEW_STATUS_UI,
  normalizeAiReviewStatus,
} from "../domain/statusAdapters";

import { AiReviewStatusBadge, renderStatusBadge } from "./RequirementReviewStatusBadge";
import { GENERATION_STATUS_BADGES } from "./RequirementsListScreen";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "./ui";

interface RequirementDetailRecord {
  id: string | number;
  title?: string;
  description?: string | null;
  sourceType?: string | null;
  sourceFileName?: string | null;
  aiReviewStatus?: AiReviewStatus | null;
  reviewStatus?: string | null;
  generatedSummary?: string | null;
  generationStatus?: string | null;
  generationTarget?: string | null;
  project?: unknown;
  createdAt?: string | null;
  updatedAt?: string | null;
  generatedArtifacts?: RequirementGeneratedArtifacts | null;
}

interface RequirementDetailScreenProps {
  entity: UiEntitySpec;
  id: string | number;
  titleOverride?: string;
  onBack?: () => void;
  onDeleted?: () => void;
}

export function RequirementDetailScreen({
  entity,
  id,
  titleOverride,
  onBack,
  onDeleted,
}: RequirementDetailScreenProps): JSX.Element {
  const { data, isLoading, error, refetch } = useEntityDetail<RequirementDetailRecord>({
    entity: entity.entityName,
    id,
    enabled: true,
  });

  const { update, isSubmitting: isUpdating, error: updateError } = useUpdateEntity<
    Partial<RequirementDetailRecord>,
    RequirementDetailRecord
  >({
    entity: entity.entityName,
    id,
  });

  const deleteMutation = useEntityDelete({
    entity: entity.entityName,
    id,
    onSuccess: () => {
      onBack?.();
      onDeleted?.();
    },
  });

  const [actionError, setActionError] = useState<string | null>(null);

  const artifacts: RequirementGeneratedArtifacts | null | undefined = data?.generatedArtifacts;
  const checklistItems: RequirementChecklistItem[] = artifacts?.checklistItems ?? [];
  const relatedWorkItems: WorkplanItem[] = artifacts?.relatedWorkItems ?? [];

  const aiReviewStatus: AiReviewStatus = useMemo(
    () => normalizeAiReviewStatus(data?.aiReviewStatus) ?? AI_REVIEW_STATUS.pending,
    [data?.aiReviewStatus],
  );

  const projectName = useMemo(() => {
    const project = data?.project;

    if (project == null) return "—";
    if (typeof project === "string") return project;
    if (typeof project === "object") {
      if ("name" in project && typeof (project as any).name === "string") {
        return (project as any).name as string;
      }
      if ("title" in project && typeof (project as any).title === "string") {
        return (project as any).title as string;
      }
    }

    return String(project);
  }, [data?.project]);

  const generationStatusBadge = useMemo(
    () => renderStatusBadge(data?.generationStatus ?? undefined, GENERATION_STATUS_BADGES),
    [data?.generationStatus],
  );

  const aiStatusLabel = useMemo(() => {
    switch (aiReviewStatus) {
      case AI_REVIEW_STATUS.completed:
        return {
          label: "AI suggestions – approved as is",
          className: "border-green-200 bg-green-50 text-green-700",
        };
      case AI_REVIEW_STATUS.in_progress:
        return {
          label: "AI suggestions – approved with edits",
          className: "border-amber-200 bg-amber-50 text-amber-700",
        };
      case AI_REVIEW_STATUS.pending:
        return {
          label: "AI suggestions – in review",
          className: "border-amber-200 bg-amber-50 text-amber-700",
        };
      case AI_REVIEW_STATUS.rejected:
        return {
          label: "AI suggestions – rejected",
          className: "border-red-200 bg-red-50 text-red-700",
        };
      case AI_REVIEW_STATUS.none:
        return {
          label: "AI suggestions – not applicable",
          className: "border-slate-200 bg-slate-50 text-slate-700",
        };
      default:
        return {
          label: "AI suggestions – not yet reviewed",
          className: "border-slate-200 bg-slate-50 text-slate-700",
        };
    }
  }, [aiReviewStatus]);

  const handleUpdateStatus = useCallback(
    async (nextStatus: AiReviewStatus) => {
      setActionError(null);
      const result = await update({ aiReviewStatus: nextStatus });

      if (!result) {
        setActionError("Failed to update review status. Please try again.");
        alert("Failed to update review status. Please try again.");
        return;
      }

      refetch();
    },
    [refetch, update],
  );

  const isStatus = useCallback(
    (status: AiReviewStatus) => aiReviewStatus === status,
    [aiReviewStatus],
  );

  const handleDelete = useCallback(() => {
    const confirmed = window.confirm("Are you sure you want to delete this item?");
    if (!confirmed) return;

    void deleteMutation.mutate();
  }, [deleteMutation]);

  const headerActions = (
    <Button type="button" variant="danger" onClick={handleDelete} disabled={deleteMutation.isLoading}>
      {deleteMutation.isLoading ? "Deleting…" : "Delete"}
    </Button>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={titleOverride ?? entity.label} onBack={onBack} actions={headerActions} />
        <Card>
          <span className="text-sm text-slate-600">Loading requirement…</span>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={titleOverride ?? entity.label} onBack={onBack} actions={headerActions} />
        <Card>
          <span className="text-sm text-red-600">
            {error instanceof Error ? error.message : "Failed to load requirement details."}
          </span>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={titleOverride ?? entity.label} onBack={onBack} actions={headerActions} />
        <EmptyState title="Requirement not found" description="The requested requirement was not found." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={data.title ?? titleOverride ?? entity.label} onBack={onBack} actions={headerActions} />

      <Card className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900">{data.title ?? "Untitled requirement"}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <AiReviewStatusBadge status={aiReviewStatus} />
              {generationStatusBadge}
            </div>
          </div>
          <p className="text-sm text-slate-600">Project: {projectName}</p>
        </div>
      </Card>

      <Card className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-slate-800">Requirement overview</h2>
          <p className="text-sm text-slate-500">Problem statement and business context.</p>
        </div>
        <div className="flex flex-col gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Title</h3>
            <p className="text-sm text-slate-900">{data.title ?? "—"}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Project</h3>
            <p className="text-sm text-slate-900">{projectName}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Description</h3>
            <div className="whitespace-pre-line text-sm text-slate-900">{data.description ?? "—"}</div>
          </div>
        </div>
      </Card>

      <Card className="flex flex-col gap-3">
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold text-slate-800">AI summary & checklist</h2>
            <p className="text-sm text-slate-500">Synthetic view of AI-generated content for this requirement.</p>
          </div>
          <span
            className={`inline-flex items-center rounded-md border px-3 py-1 text-xs font-semibold ${aiStatusLabel.className}`}
          >
            {aiStatusLabel.label}
          </span>
        </div>

        {data.generatedSummary ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-slate-700">AI summary</h3>
            <p className="text-sm text-slate-800 whitespace-pre-line">{data.generatedSummary}</p>
          </div>
        ) : null}

        {checklistItems.length > 0 ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-slate-700">AI checklist</h3>
            <div className="overflow-x-auto">
              <Table dense>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Category</TableHeaderCell>
                    <TableHeaderCell>Checklist item</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checklistItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-slate-600">{item.category ?? "—"}</TableCell>
                      <TableCell className="text-slate-800">{item.label}</TableCell>
                      <TableCell className="text-slate-600">{item.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}

        {!data.generatedSummary && checklistItems.length === 0 ? (
          <p className="text-sm text-slate-500">
            No AI-generated summary or checklist for this requirement yet.
          </p>
        ) : null}
      </Card>

      <Card className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-slate-800">AI review status</h3>
            <p className="text-sm text-slate-500">
              Use this status to validate or request changes on the AI-generated content for this requirement.
            </p>
          </div>
          <AiReviewStatusBadge status={aiReviewStatus} />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void handleUpdateStatus(AI_REVIEW_STATUS.completed)}
            disabled={isUpdating || isStatus(AI_REVIEW_STATUS.completed)}
          >
            Approve as is
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void handleUpdateStatus(AI_REVIEW_STATUS.in_progress)}
            disabled={isUpdating || isStatus(AI_REVIEW_STATUS.in_progress)}
          >
            Approve with edits
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleUpdateStatus(AI_REVIEW_STATUS.pending)}
            disabled={isUpdating || isStatus(AI_REVIEW_STATUS.pending)}
          >
            Reset to pending
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleUpdateStatus(AI_REVIEW_STATUS.rejected)}
            disabled={isUpdating || isStatus(AI_REVIEW_STATUS.rejected)}
          >
            Mark as rejected
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleUpdateStatus(AI_REVIEW_STATUS.none)}
            disabled={isUpdating || isStatus(AI_REVIEW_STATUS.none)}
          >
            Mark as not applicable
          </Button>
        </div>

        {updateError ? (
          <p className="text-sm text-red-600">
            {updateError instanceof Error
              ? updateError.message
              : "Failed to update review status. Please try again."}
          </p>
        ) : null}

        {actionError ? (
          <p className="text-sm text-red-600">{actionError}</p>
        ) : null}
      </Card>

      {relatedWorkItems.length > 0 ? (
        <Card className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-slate-800">Related work items (AI)</h3>
            <p className="text-sm text-slate-500">
              Suggested work items linked to this requirement.
            </p>
          </div>

          <div className="overflow-x-auto">
            <Table dense>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Title</TableHeaderCell>
                  <TableHeaderCell>Category</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatedWorkItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-slate-800">{item.title}</TableCell>
                    <TableCell className="text-slate-600">{item.category ?? "—"}</TableCell>
                    <TableCell className="text-slate-600">{item.status ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : null}

      <Card className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-slate-800">Metadata & raw data</h2>
          <p className="text-sm text-slate-500">Context and debugging information.</p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <p className="text-xs uppercase text-slate-500">Source Type</p>
            <p className="text-sm text-slate-900">{data.sourceType ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Source File Name</p>
            <p className="text-sm text-slate-900">{data.sourceFileName ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Created At</p>
            <p className="text-sm text-slate-900">{data.createdAt ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Updated At</p>
            <p className="text-sm text-slate-900">{data.updatedAt ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Generation Target</p>
            <p className="text-sm text-slate-900">{data.generationTarget ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Generation Status</p>
            <div className="mt-1 flex items-center gap-2">{generationStatusBadge || "—"}</div>
          </div>
        </div>

        <details className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          <summary className="cursor-pointer font-semibold text-slate-700">
            Raw AI artifacts (debug)
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-xs text-slate-700">
            {artifacts ? JSON.stringify(artifacts, null, 2) : "No artifacts available."}
          </pre>
        </details>
      </Card>
    </div>
  );
}
